import PropTypes from "prop-types";
import { useContext } from "react";
import { DesignAssetContext } from "../../pages/CustomHooks/ContextApi";
import useCBMouserOver from "../../pages/CustomHooks/useCBMouserOver";
import embLabels from "../../config/emb-labels-en.json";
import GroundingTransformerSLD from "../ETOIslandable/GroundingTransformerSLD";

// SVG Constants
const SVG_CONFIG = {
  NEW_SWITCHBOARD: {
    x: 380,
    y: 370,
    cbHeight: "100",
    cbY: 22,
    gtLineY1: "90",
  },
  STANDARD_SWITCHBOARD: {
    x: 610,
    y: 470,
    cbHeight: "100",
    cbY: 10,
    gtLineY1: "105",
  },
};

// Color Constants
const COLORS = {
  LINE_COLOR: "#E47F00",
  HIGHLIGHT_COLOR: "#42B4E6",
  CIRCUIT_LINE: "var(--se-sld-circline)",
  BACKGROUND: "var(--se-background-alternative)",
  FILL_HOVER: "rgba(65, 181, 230, 0.35)",
  FILL_SELECTION: "rgba(0, 135, 205, 0.2)",
};

// SVG Dimensions
const SVG_DIMENSIONS = {
  CB: {
    width: 70,
    viewBox: "0 0 10 149",
    x: 44,
    y: 0,
    rectWidth: 85,
    strokeWidth: 4,
  },
  GT: {
    width: 149,
    height: 468,
    viewBox: "0 0 90 550",
    x: 0,
    rectX: 0,
    rectY: 95,
    rectWidth: 96,
    rectHeight: 148,
    circleX: 48,
    circleY: 193.5,
    outerRadius: 55,
    innerRadius: 46.5,
    strokeWidth: 3,
  },
};

/**
 * Determines SVG configuration based on switchboard type
 * @param {string} switchboardType - Type of switchboard
 * @returns {Object} SVG configuration object
 */
const getSvgConfig = (switchboardType) => {
  return switchboardType === embLabels.NEW_SWITCHBOARD
    ? SVG_CONFIG.NEW_SWITCHBOARD
    : SVG_CONFIG.STANDARD_SWITCHBOARD;
};

/**
 * GroundingTransformer Component
 * Renders an SVG diagram for a Grounding Transformer with its associated Circuit Breaker
 */
const GroundingTransformer = ({x, y,lineHieght}) => {
  const [mouseOverData, CBMouseHandler] = useCBMouserOver();

  // Destructure context with null checks
  const context = useContext(DesignAssetContext);
  if (!context) {
    console.warn("GroundingTransformer: DesignAssetContext is not available");
    return null;
  }

  const {
    CBGTList = [{}],
    switchboardType = embLabels.NEW_SWITCHBOARD,
    gtSelectHandler = () => {},
    loadSide
  } = context;

  const [{ uiData = {}, configData = {}, cbConfigData = {} } = {}] = CBGTList;
  const { CBSelected = false, CBConfigured = false } = uiData;

  const svgConfig = getSvgConfig(switchboardType);
  if(!uiData.status){
      return null;
  }
  return (
    <>
   { uiData.status?
    <svg className="pointer" x={x ??svgConfig.x} y={y ?? svgConfig.y}>
      {/* ✅ Circuit Breaker (CB GT) */}
      <g id="CB_GT">
        <svg
          width={SVG_DIMENSIONS.CB.width}
          height={svgConfig.cbHeight}
          viewBox={SVG_DIMENSIONS.CB.viewBox}
          x={SVG_DIMENSIONS.CB.x}
          y={SVG_DIMENSIONS.CB.y}
        >
          <title>{cbConfigData.name}</title>
          <g fill="none" stroke="none">
            <g transform="translate(-651 -512)">
              <g
                transform="translate(604 514)"
                // onMouseOver={CBMouseHandler}
                // onMouseOut={CBMouseHandler}
              >
                <g
                  strokeWidth={SVG_DIMENSIONS.CB.strokeWidth}
                  // fill={COLORS.FILL_HOVER}
                  // stroke={CBSelected ? COLORS.HIGHLIGHT_COLOR : "none"}
                >
                  <rect
                    x="5"
                    y="8"
                    width={SVG_DIMENSIONS.CB.rectWidth}
                    height={svgConfig.cbHeight}
                  />

                  <g
                    transform="translate(48, 0)"
                    stroke={CBConfigured ? COLORS.CIRCUIT_LINE : COLORS.LINE_COLOR}
                    strokeLinecap="round"
                    strokeWidth={SVG_DIMENSIONS.CB.strokeWidth}
                  >
                    <line x1="0.5" y1="0" x2="0.5" y2="47.83" />
                  </g>
                </g>

                <line
                  x1="48.5"
                  y1="97.17"
                  x2="48.5"
                  y2="145"
                  stroke={CBConfigured ? COLORS.CIRCUIT_LINE : COLORS.LINE_COLOR}
                  strokeWidth={SVG_DIMENSIONS.CB.strokeWidth}
                  strokeLinecap="round"
                />

                <path
                  d="M49,48.66 C56.94,64.55 56.94,80.55 49,96.66"
                  stroke={CBConfigured ? COLORS.CIRCUIT_LINE : COLORS.LINE_COLOR}
                  strokeWidth={SVG_DIMENSIONS.CB.strokeWidth}
                  strokeLinecap="round"
                />
              </g>
            </g>
          </g>
        </svg>
      </g>

      {/* ✅ Grounding Transformer */}
      <g id="GT">
        <svg
          width={SVG_DIMENSIONS.GT.width}
          height={SVG_DIMENSIONS.GT.height}
          viewBox={SVG_DIMENSIONS.GT.viewBox}
          x={SVG_DIMENSIONS.GT.x}
          y={svgConfig.cbY}
          onClick={gtSelectHandler}
        >
          <title>{embLabels.GROUNDING_TRANSFORMER || "Grounding Transformer"}</title>
          <g fill="none" stroke="none">
            <g transform="translate(-604 -372)">
              <g transform="translate(604 372)">
                <g>
                  <g>
                    <rect
                      x={SVG_DIMENSIONS.GT.rectX}
                      y={SVG_DIMENSIONS.GT.rectY}
                      width={SVG_DIMENSIONS.GT.rectWidth}
                      height={SVG_DIMENSIONS.GT.rectHeight}
                    />

                    <line
                      x1="48"
                      y1={loadSide ==="LINE_SIDE"?lineHieght??"91":svgConfig.gtLineY1}
                      x2="48"
                      y2="144"
                      strokeWidth={SVG_DIMENSIONS.GT.strokeWidth}
                      strokeLinecap="round"
                      stroke={COLORS.CIRCUIT_LINE}
                    />

                    {/* Selection highlight circle */}
                    <circle
                      cx={SVG_DIMENSIONS.GT.circleX}
                      cy={SVG_DIMENSIONS.GT.circleY}
                      r={SVG_DIMENSIONS.GT.outerRadius}
                      strokeWidth={SVG_DIMENSIONS.GT.strokeWidth}
                      fill={COLORS.FILL_SELECTION}
                      stroke={COLORS.HIGHLIGHT_COLOR}
                      className="hidden"
                    />

                    {/* Main transformer circle */}
                    <circle
                      cx={SVG_DIMENSIONS.GT.circleX}
                      cy={SVG_DIMENSIONS.GT.circleY}
                      r={SVG_DIMENSIONS.GT.innerRadius}
                      fill={COLORS.BACKGROUND}
                      stroke={COLORS.CIRCUIT_LINE}
                      strokeWidth={SVG_DIMENSIONS.GT.strokeWidth}
                    />

                    {/* Grounding Transformer Symbol */}
                    {/* <g>
                      <GroundingTransformerSLD />
                    </g> */}
                    <text
                        id="GT"
                        fontSize="22"
                        fontWeight="normal"
                        stroke="var(--se-sld-circline)"
                        fontFamily="Nunito-Regular, Nunito"
                        fill="var(--se-sld-circline)"
                    >
                        <tspan x="33" y="203">
                        GT
                        </tspan>
                    </text>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </g>
    </svg>
    :null}
    </>
  );
};

// PropTypes validation
GroundingTransformer.propTypes = {};

GroundingTransformer.displayName = "GroundingTransformer";

export default GroundingTransformer;